import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { logger } from "../lib/logger";

const AuthContext = createContext(null);

// Stale token from the previous (localStorage) auth scheme. Cleaned up once
// on mount so existing browsers don't keep a token sitting in storage.
const LEGACY_TOKEN_KEY = "vmtips_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // 401 on first load is expected for anonymous visitors — keep it quiet.
            if (e?.response?.status !== 401) {
                logger.warn("auth: /me failed", e);
            }
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // One-time migration: nuke any token left over from the old localStorage scheme.
        try {
            localStorage.removeItem(LEGACY_TOKEN_KEY);
        } catch (e) {
            logger.warn("auth: localStorage cleanup failed", e);
        }
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        return data;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        setUser(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            logger.warn("logout request failed", e);
        }
        setUser(false);
    }, []);

    // Memoize so context consumers don't re-render every time AuthProvider re-renders.
    const value = useMemo(
        () => ({ user, loading, login, register, logout, refresh }),
        [user, loading, login, register, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
